<script>
import { get, isBoolean, isPlainObject } from '@lsfe/tools';
import Stickyfill from 'stickyfilljs';
import Sortable from 'sortablejs';
// import { defaultCoreCipherList } from 'constants';
import { getRowSpanReg, arraySpanMethod, tableDataSplitor } from './utils';
import ColumnSetting from './columnSetting';
import FontSetting from './fontSetting';
import { Formaters } from './templates';
import { getEle, setStyle } from './setFixed.js';

const TABLE_STATUS = {
    LOADING: 'loading',
    DONE: 'done',
    ERROR: 'error'
};

function prefixLog(text) {
    return `[Table Pro]${text}`;
}

const defaultSortableJsOptions = {
    chosenClass: 'table-pro-drag-chosen-row-class',
    dragClass: 'table-pro-drag-ing-row-class',
    ghostClass: 'table-pro-drag-ghost-row-class',
};

/**
 *  @author chenjie17
 *  @since 1.3.2
 *
 *
 *  基于 el-table 做的，列的配置和el-table 兼容, 集成分页，表头配置等，可以支持本地和远程数据两种形式, 一些表格相关的规范
 *  如果有操作列，操作列固定，fixed
 *
 */
export default {
    name: 'XxTablePro',
    components: {
        ColumnSetting,
        FontSetting,
        ...Formaters
    },
    props: {
        sticky: {
            default: () => {
                return false;
            }
        }, // 如果开启头sticky,配置 {zIndex: number || 1, position: 'top', offset: 0}
        name: {
            type: String,
            required: true
        },
        columns: {
            type: Array,
            default: () => []
        },
        // 使用 sortable.js 做拖拽
        enableDnd: {
            type: Boolean,
            default: false
        },
        // 使用 sortable.js 选项
        sortableJsOptions: {
            type: Object,
            default: () => ({ ...defaultSortableJsOptions })
        },
        /**
         * 来源类型, [local, remote], 默认值是remote
         */
        source: {
            type: String,
            default: 'remote' // 'local', 'remote'
        },
        /**
         * 本地数据源
         * //不知道为什么data会被$attr吞噬
         * todo 传了data的都是本地的、本地分页、本地滚动加载
         */
        localData: {
            type: Array,
            default: () => {
                return [];
            }
        },

        /**
         * 分页设置， 默认值见paginationDef
         */
        pagination: {
            type: [Boolean, Object],
            default: true
        },

        headerConfigure: {
            type: Object,
            default: () => {
                return {
                    enable: false
                };
            }
        },
        /**
         * 字体设置
         */
        fontSetting: {
            type: Boolean,
            default: false
        },

        /**
         * 请求
         */
        ajax: [String, Object, Function],

        /**
         * source为remote的时候是否自动加载数据
         */
        autoLoad: {
            type: Boolean,
            default: true
        },
        /**
         * 将源数据处理成表格数据
         */
        responseProcessor: {
            type: Function,
            default: res => {
                if (res.list) {
                    return {
                        data: res.list,
                        total: res.total
                    };
                }
                return {
                    data: res.dataList,
                    total: res.paging.total
                };
            }
        },
        /**
         *  用来自定义组装参数
         */
        buildParams: {
            type: Function
        },
        tableClass: {
            type: String,
            default: ''
        },

        /**
         *
         *
         */

        editable: false,

        /**
         * table将需要根据这个key，将key值一样的行做rowSpan合并
         * 做行合并时，必须配置这个参数
         */
        rowSpanKeyName: {
            type: String,
            default: ''
        },

        /**
         * spanMethod覆盖el-table的spanMethod
         * 做行合并的时候一般不需要设置这个函数而是设置noSliceRemoteData和splitRemoteListDataProp
         */
        spanMethod: {
            type: Function
        },
        /**
         * 是否允许拆分远程列表数据，即把spu条目拆分为sku条目，并不改变总条目数，默认是false。
         * 做行合并时一般不需要配置他，因为只要设置了rowSpanKeyName，就默认可以拆分
         */
        noSliceRemoteData: {
            type: Boolean,
            default: false
        },
        /**
         * 把spu条目拆分为sku条目时，指定每一个spu中的sku列表的属性的名字，例如‘skuBaseList’
         * 需要做行拆分时，必须配置
         */
        splitRemoteListDataProp: {
            type: [String, Function],
            default: ''
        },
        /**
         * 是否开启表头吸顶功能
         */
        fixedHeader: {
            type: Boolean,
            default: true
        },
        /**
         * 表格吸顶相关配置对象，仅当fixedHeader为true时生效。具体有：
         * 吸顶距离：offset 默认为0
         * 表头层级：zIndex： 默认为10
         * 例如：{
         *  offset: 200,
         *  zIndex: 10,
         * }
         */
        fixedHeaderOptions: {
            type: Object,
            default: () => {
                return {
                    offset: 0,
                    zIndex: 1
                };
            }
        },
        autoReportFmp: {
            type: Boolean,
            default: false
        },
        autoSortColumns: {
            type: Boolean,
            default: true
        },
        // 拖拽后自动记录列宽
        autoRecordColumnsWidth: {
            type: Boolean,
            default: false
        },
        // 是否使用localStorage记录列的设置
        useLocalStorage: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            remoteData: [],
            // 把分页的统一放在一个对象里面，table表格
            status: TABLE_STATUS.DONE,
            currentPage: 1,
            pageSize: 0, // 初始默认值为0， 会在paginationDef计算后给值
            total: 0,
            queryParams: {},

            /** 行列聚合相关的 */
            rowSpanReg: [],

            /** 表格内编辑相关的 , */
            validateInputList: [],
            columnSeting: null,
            remoteColumns: [], // 接口返回的配置信息
            columnsWidthMap: {} // 拖拽后列宽度记录
        };
    },
    computed: {
        tableKey() {
            return `${window.location.pathname}-table-${this.name}`;
        },
        curTableData() {
            let tableDataTmp = null;
            let { pageSize, currentPage } = this;
            if (!pageSize) return [];
            if (this.source === 'local') {
                let [start, end] = [pageSize * (currentPage - 1), pageSize * currentPage];
                tableDataTmp = this.localData.slice(start, end);
            } else if (this.noSliceRemoteData || this.rowSpanKeyName) {
                tableDataTmp = this.remoteData;
            } else {
                tableDataTmp = this.remoteData.slice(0, pageSize);
            }
            this.$emit('TableDataChange', tableDataTmp);
            // 当列的顺序变化（如拖动）时，需要重新计算rowSpanReg，现在还没有监听列变化的事件，所以先放在这里
            this.rowSpanReg = getRowSpanReg(this.rowSpanKeyName, tableDataTmp);
            return tableDataTmp;
        },

        paginationDef() {
            let pagination = isBoolean(this.pagination) ? { show: this.pagination } : this.pagination;

            let { show, layout, pageSize, pageSizes } = pagination;
            let total = this.source === 'local' ? this.localData.length : this.total;
            let pageSizeDef = show ? (this.pageSize ? this.pageSize : pageSize || (pageSizes && pageSizes[1]) || 20) : total;
            return Object.assign({}, pagination, {
                layout: layout || 'sizes, total, ->, prev, pager, next, jumper',
                pageSize: pageSizeDef,
                pageSizes: (pageSizes && (!pageSize || pageSizes.includes(pageSize)) && pageSizes) || (pageSize && [pageSize]) || [10, 20, 50, 100],
                currentPage: this.currentPage || 1,
                total: total
            });
        },

        isLoading() {
            return this.status === TABLE_STATUS.LOADING;
        },

        maskShown() {
            return this.status === TABLE_STATUS.ERROR;
        },
        /**
          * 按页面上实际展示的column顺序组织的列信息，把fixed:'left'的放左边，fixed:'right'的放右边
          */
        innerColumnsShow() {
            let v = this.innerColumns;
            let leftFixedColumns = v.filter(i => i.fixed === 'left' || i.fixed === true);
            let rightFixedColumns = v.filter(i => i.fixed === 'right');
            let noFixedColumns = v.filter(i => i.fixed !== 'right' && i.fixed !== 'left' && i.fixed !== true);
            return [...leftFixedColumns, ...noFixedColumns, ...rightFixedColumns];
        },
        hasPrefix() {
            return this.$slots.prefix || this.$slots.prefixRight || this.headerConfigure.enable || this.fontSetting;
        },
        innerColumns() {
            const storageColumns = this.useLocalStorage ? this.columnSeting || this.$storage.get(this.tableKey) || [] : [];
            const { remoteColumns = [], columns = [] } = this;

            const columnsPropMap = columns.reduce((prev, cur) => {
                if (cur.prop) {
                    prev[cur.prop] = cur;
                }
                return prev;
            }, {});

            remoteColumns.forEach(item => {
                if (columnsPropMap[item.prop]) {
                    // 存在 覆盖
                    return Object.assign(columnsPropMap[item.prop], item);
                }

                const { _index } = item;
                if (_index !== null && _index > -1) {
                    // _index存在 则添加到指定位置
                    return columns.splice(_index, 0, item);
                }

                columns.push(item);
            });

            if (!this.headerConfigure || !this.headerConfigure.enable) {
                return columns;
            }

            const storageColumnsPropMap = (storageColumns || []).reduce((prev, cur) => {
                prev[cur.prop] = cur;
                return prev;
            }, {});

            const newColumns = columns.map(column => {
                const storeInfo = storageColumnsPropMap[column.prop];
                if (storeInfo) {
                    Object.assign(column, { visible: storeInfo.visible, width: storeInfo.width });
                }
                return column;
            });
            if (storageColumns.length) {
                // 如果本地有设置  则根据设置排序 新列加载最后
                newColumns.sort((a, b) => {
                    const indexA = storageColumns.findIndex(v => v.prop === a.prop);
                    const indexB = storageColumns.findIndex(v => v.prop === b.prop);
                    if (indexA > -1 && indexB > -1) {
                        // 如果本地存储有 则按照本地存储顺序排
                        return indexA - indexB;
                    }
                    if(!this.autoSortColumns) {
                        return 0;
                    }

                    // 如果是后端返回或新加的列 则排到最后
                    return -1;
                });
            }

            return newColumns;
        }
    },
    methods: {
        setColumnWidth(columns) {
            return columns.map(column => {
                const {prop} = column;
                const width = this.columnsWidthMap[prop];
                if(width) {
                    column.width = width;
                }
                return column;
            });
        },
        headerDragend(newWidth, oldWidth, column, event) {
            const { property } = column;
            if(newWidth > 0) {
                this.columnsWidthMap[property] = Math.ceil(newWidth);
            }
            if(this.autoRecordColumnsWidth) {
                this.setColumn(this.innerColumns);
            }
            this.$emit('header-dragend', newWidth, oldWidth, column, event);
        },
        arraySpanMethod({ row, column, rowIndex, columnIndex }) {
            return arraySpanMethod(this.rowSpanReg, this.innerColumnsShow, {
                row,
                column,
                rowIndex,
                columnIndex
            });
        },
        getPagingData(needCurrentPage) {
            const paging = {
                offset: this.pageSize * (this.currentPage - 1),
                limit: this.pageSize
            };

            if (needCurrentPage) {
                paging.currentPage = this.currentPage;
            }

            return paging;
        },

        handleCurrentChange(val) {
            let request;
            if (this.source === 'local') {
                this.currentPage = val;
            } else {
                request = this.fetchData(val);
            }
            this.$emit('current-page-change', val, { request });
        },
        handleSizeChange(val) {
            this.pageSize = val;
            this.$emit('page-size-change', val);
            if (this.source === 'remote') {
                this.fetchData();
            }
        },

        /**
         * 保存列的设置：排序、可见
         * @param columns
         */
        setColumn(columns) {
            let willStorageColumns = columns;
            if(this.autoRecordColumnsWidth) {
                willStorageColumns = this.setColumnWidth(columns);
            }
            if(this.useLocalStorage) {
                this.$storage.set(this.tableKey, willStorageColumns);
            }
            this.columnSeting = columns;
            this.$emit('columnsChange', columns);
        },
        handleChangeFontSize(isBigger) {
            const target = getEle('el-table', this.$el)[0];
            if (isBigger) {
                if (target.style.zoom) {
                    target.style.zoom = target.style.zoom / 1 + 0.05;
                } else {
                    target.style.zoom = 1.05;
                }
            } else {
                if (target.style.zoom >= 1) {
                    target.style.zoom -= 0.05;
                }
            }
            this.$storage.set(`${this.tableKey}-font`, target.style.zoom);
        },
        queryData(params) {
            if (params) {
                // 这里直接覆盖，因为除分页之外的信息，都由父组件维护。今后如果有过滤，排序的信息，类似分页信息由table维护，而不由父组件传入
                this.queryParams = params;
            }
            this.fetchData();
        },
        /**
         *  目前tableParams 只含有一个paging
         */
        _buildParam(params, tableParams) {
            let buildParams =
                this.buildParams ||
                function (params, tableParams) {
                    if (!params.request) {
                        params.paging = tableParams.paging;
                    } else {
                        params.request.paging = tableParams.paging;
                    }
                    return params;

                    // return Object.assign(params, tableParams);
                };
            return buildParams(params, tableParams);
        },

        /**
         * 获取数据
         */
        async fetchData(curPage = 1) {
            this.status = TABLE_STATUS.LOADING;
            this.currentPage = curPage;
            let promise;
            if (typeof this.ajax === 'string') {
                promise = this.$get(this.ajax);
            } else if (typeof this.ajax === 'object') {
                let { url, type = 'post', params, promiseFn = null } = this.ajax;
                // 如果从queryData 发起搜索，则覆盖ajax原有的params
                if (Object.keys(this.queryParams).length) {
                    params = this.queryParams;
                }
                if (!url && !promiseFn) {
                    console.error(prefixLog('url未定义'));
                    return;
                }
                if (params) {
                    if (!isPlainObject(params)) {
                        console.error(prefixLog('params必须是个对象'));
                        return;
                    }

                    params = await this._buildParam(params, {
                        paging: this.getPagingData()
                    });
                }
                if (promiseFn) {
                    promise = promiseFn(params);
                } else {
                    if (!['hGet', 'hPost'].includes(type)) {
                        type = type.toLowerCase();
                    }
                    promise = this[`$${type}`](url, params);
                }
            } else if (typeof this.ajax === 'function') {
                promise = this.ajax(this.getPagingData());
            } else {
                console.error(prefixLog('source为remote时，ajax未定义！'));
                return;
            }
            return promise
                .then(async res => {
                    let { config = {} } = res;
                    let { columns = [] } = config;
                    res = await this.responseProcessor(res);
                    let { data, total } = res;
                    if (res.config && res.config.columns) {
                        config = res.config;
                        columns = res.config.columns;
                    }
                    total = +total;
                    if (!data || (this.paginationDef.show && total === undefined)) {
                        console.warn(prefixLog('response返回格式异常'));
                    }
                    data = data || [];
                    if (this.splitRemoteListDataProp) {
                        data = tableDataSplitor(data, this.splitRemoteListDataProp);
                    }
                    this.remoteData = data;
                    this.total = total;
                    this.status = TABLE_STATUS.DONE;
                    this.remoteColumns = columns;
                    this.$emit('remoteDataChange', { total, data, config });
                })
                .catch(e => {
                    // 这里最好是在表格里面能显示出来错误，而不是toastr一下
                    // 用户可以配置错误内容

                    this.$message.error((e && (e.msg || e.message)) || '发生错误');
                    this.status = TABLE_STATUS.ERROR;
                    this.$emit('load-error', e);
                    console.error(prefixLog(e));
                });
        },

        /**
         * 维持当前信息重新获取，比如在当前页面进行刷新
         */
        reloadData() {
            return this.fetchData(this.currentPage);
        },
        // 主动清空数据
        clearData() {
            if(this.source === 'remote') {
                this.remoteData = [];
            }
            if(this.source === 'local') {
                this.localData = [];
            }
            this.total = 0;
        },

        // 以下为映射el-table的实例方法
        clearSelection() {
            this.$refs.elTable.clearSelection();
        },

        toggleRowSelection(row, selected) {
            this.$refs.elTable.toggleRowSelection(row, selected);
        },

        toggleRowExpansion(row, expanded) {
            this.$refs.elTable.toggleRowExpansion(row, expanded);
        },

        setCurrentRow(row) {
            this.$refs.elTable.setCurrentRow(row);
        },

        clearSort() {
            this.$refs.elTable.clearSort();
        },

        clearFilter() {
            this.$refs.elTable.clearFilter();
        },

        doLayout() {
            this.$refs.elTable.doLayout();
        },

        sort(prop, order) {
            this.$refs.elTable.sort(prop, order);
        },

        /**
         * 选中所有
         */
        allSelection() {
            const data = this.curTableData || [];
            if (data.length === 0) {
                return;
            }

            data.forEach((item, index) => {
                this.$refs.elTable.toggleRowSelection(item, true);
            });
        },

        async validateAllInput() {
            const task = this.validateInputList.map(validInputVm => validInputVm.validate());
            const validRes = await Promise.all(task);
            const errors = validRes.filter(error => error);
            if (errors.length) {
                const messageList = Array.prototype.concat
                    .apply([], errors)
                    .filter(item => item && item.message)
                    .map(item => item.message);
                const messages = new Set(messageList);
                return {
                    errors,
                    messages: [...messages]
                };
            }
            return null;
        },

        getFontSizeFromStorage() {
            const storageFontSize = this.$storage.get(`${this.tableKey}-font`);
            const target = getEle('el-table', this.$el)[0];
            target.style.zoom = storageFontSize;
        },

        forceUpdate() {
            this.$refs.elTable.$forceUpdate();
        },

        renderHeader(h, columns = []) {
            return columns.map((column, index) => {
                // 多级表头
                if (column.children && column.children.length) {
                    const { label, visible = true, ...otherProps } = column;
                    if (!visible) {
                        return '';
                    }
                    return (
                        <el-table-column label={label} {...{ attrs: otherProps }}>
                            {this.renderHeader(h, column.children)}
                        </el-table-column>
                    );
                }

                let { type, label, prop, template = 'default', visible = true, help, ...colProps } = column;
                if (visible) {
                    let vnode;
                    const { pageSize, currentPage } = this.paginationDef;
                    if (['selection', 'index'].includes(type)) {
                        if (!('align' in column)) {
                            colProps.align = 'center';
                        }

                        if (column.reserveIndex && type === 'index') {
                            vnode = scope => {
                                const { pageSize, currentPage } = this.paginationDef;
                                let _index = (currentPage - 1) * pageSize + scope.$index + 1;
                                if (typeof column.index === 'function') {
                                    // 兼容element 的index函数
                                    _index = column.index(_index);
                                }
                                return <span>{_index}</span>;
                            };
                        }
                    } else if (this.$scopedSlots[prop]) {
                        // scope 回传分页信息
                        vnode = scope => this.$scopedSlots[prop]({ ...scope, pageSize, currentPage, offset: (currentPage - 1) * pageSize });
                    } else {
                        vnode = scope => {
                            if (typeof template === 'string') {
                                template = {
                                    tpl: 'default',
                                    placeholder: '--'
                                };
                            }
                            let { tpl, ...templateProps } = template;
                            const cellValue = get(scope.row, prop);
                            let reserves = ['default', 'price', 'datetime', 'enum', 'link', 'dicCode', 'array', 'html', 'image'];
                            if (typeof tpl === 'string' && reserves.includes(tpl)) tpl = `format-${tpl}`;
                            return [
                                h(tpl, {
                                    props: {
                                        value: column.formatter ? column.formatter(scope.row, column, cellValue, scope.$index) : cellValue,
                                        scope: scope,
                                        ...templateProps
                                    }
                                })
                            ];
                        };
                    }
                    // 渲染带提示的表头
                    const renderHelpHeader = (h, { column: { label } }) => {
                        if (!help) {
                            return h('span', label);
                        }
                        let helpNodes = help;
                        if(help && help.html) {
                            return (
                                <div domPropsInnerHTML={help.html} ></div>
                            );
                        }
                        if (Array.isArray(help)) {
                            helpNodes = help.map(text => h('p', text));
                        }
                        return h('span', [
                            label,
                            h('el-tooltip', { style: { marginLeft: '5px' }, attrs: { effect: 'light' } }, [
                                h('template', { slot: 'content' }, helpNodes),
                                h('i', { class: { 'el-icon-info': true } })
                            ])
                        ]);
                    };

                    if (prop === 'operations') {
                        column.fixed = column.fixed || 'right';
                        column.width = column.width || 160;
                    }
                    if (this.$mcCream && this.$mcCream.isCreamOn) {
                        if (colProps.fixed) {
                            colProps = {...colProps, fixed: false};
                        }
                    }

                    return (
                        <el-table-column type={type} label={label} prop={prop} key={prop + String(index)} renderHeader={renderHelpHeader} {...{ attrs: colProps }}>
                            {vnode}
                        </el-table-column>
                    );
                }
            });
        },
        // 更新某一行
        updateRow(row, rk) {
            const sourceData = this.source === 'local' ? this.localData : this.remoteData;
            let { rowKey = rk } = this.$attrs;

            if (!rowKey) {
                return;
            }

            const index = sourceData.findIndex(item => {
                if (typeof rowKey === 'function') {
                    return rowKey(item) === rowKey(row);
                }
                return item[rowKey] === row[rowKey];
            });

            if (index > -1) {
                this.$set(sourceData, index, { ...sourceData[index], ...row });
            }
        },
        installSortableJs() {
            const rowKey = this.$attrs.rowKey || this.$attrs['row-key'];
            if (!rowKey) {
                console.error(prefixLog('拖拽功能必须指定 rowKey'));
            }
            const options = { ...defaultSortableJsOptions, ...this.sortableJsOptions };
            if (!options.doNotComposeOnEndCallback) {
                options.onEnd = this.composeSortableJsOnEnd(options.onEnd);
            }
            this._sortable = Sortable.create(
                this.$refs.elTable.$el.querySelector('.el-table__body-wrapper tbody'), options
            );
        },
        updateSortableOptions(newOptions = {}) {
            if (this._sortable) {
                const options = { ...defaultSortableJsOptions, ...newOptions };
                if (options.onEnd && !options.doNotComposeOnEndCallback) {
                    options.onEnd = this.composeSortableJsOnEnd(options.onEnd);
                }
                Object.keys(options).forEach(property => this._sortable.option(property, options[property]));
            }
        },
        uninstallSortableJs() {
            if (this._sortable) {
                this._sortable.destroy();
                this._sortable = null;
            }
        },
        __sortableJsOnEnd({ newIndex, oldIndex }) {
            if (this.source === 'remote') {
                this.remoteData.splice(newIndex, 0, this.remoteData.splice(oldIndex, 1)[0]);
            } else {
                this.localData.splice(newIndex, 0, this.localData.splice(oldIndex, 1)[0]);
            }
        },
        composeSortableJsOnEnd(func = () => {}) {
            return (...args) => {
                this.__sortableJsOnEnd(...args);
                func(...args);
            };
        }
    },
    provide() {
        return {
            registerValidInput: vm => {
                this.validateInputList.push(vm);
            },
            unloadValidInput: vm => {
                const index = this.validateInputList.findIndex(item => item === vm);
                if (index > -1) {
                    this.validateInputList.splice(index, 1);
                }
            }
        };
    },
    watch: {
        paginationDef: {
            immediate: true,
            handler(def) {
                this.currentPage = def.currentPage;
                this.pageSize = def.pageSize;
                this.total = def.total;
            }
        },
        enableDnd(val) {
            this.uninstallSortableJs();
            if (val) {
                this.$nextTick(() => this.installSortableJs());
            }
        },
        sortableJsOptions(options) {
            this.updateSortableOptions(options);
        }
    },
    created() {
        // 收集columns
        this.source === 'remote' && this.autoLoad && this.fetchData();
    },
    mounted() {
        let sticky = this.sticky;
        if (sticky) {
            let element = this.$refs.elTable.$el.querySelector('.el-table__header-wrapper');
            let { zIndex = 1, position = 'top', offset = 0 } = sticky;
            element.style = `z-index:${zIndex};${position}: ${offset}`;
            Stickyfill.add(element);
        }

        if (this.fixedHeader) {
            setStyle(this.$el, this.fixedHeaderOptions, this.hasPrefix);
            window.addEventListener('resize', () => {
                setStyle(this.$el, this.fixedHeaderOptions, this.hasPrefix);
            });
        }

        if (this.fontSetting) {
            this.getFontSizeFromStorage();
        }

        if (this.enableDnd) {
            this.installSortableJs();
        }
    },
    beforeDestroy() {
        this.uninstallSortableJs();
    },
    render(h) {
        const HeaderConfing = this.renderHeader(h, this.innerColumns);

        if (this.autoReportFmp) {
            // 添加隐藏列
            HeaderConfing.push(
                <el-table-column width={0}>
                    {() => <span fmptag></span>}
                </el-table-column>
            );
        }

        return (
            <div
                class={{
                    'table-pro': true,
                    sticky: this.sticky
                }}
                v-loading={this.isLoading}
            >
                {this.hasPrefix ? (
                    <div class="table-prefix">
                        <div>{this.$slots.prefix}</div>
                        <div>
                            {this.$slots.prefixRight}
                            {this.fontSetting ? <font-setting changeSize={this.handleChangeFontSize} /> : null}
                            {this.headerConfigure.enable ? (
                                <column-setting
                                    columns={this.innerColumns}
                                    config={this.headerConfigure}
                                    saveHandler={this.setColumn}
                                    class={this.headerConfigure.class}
                                />
                            ) : null}
                        </div>
                    </div>
                ) : null}
                <div
                    class="back-to-top"
                    onClick={() => {
                        document.getElementsByClassName('layout-page-content')[0].scrollTop = 0;
                    }}
                >
                    <i class="el-icon-caret-top" />
                </div>
                <el-table
                    ref="elTable"
                    border={true}
                    class={['color-header', this.$props.tableClass]}
                    data={this.curTableData}
                    spanMethod={this.spanMethod || this.arraySpanMethod}
                    stripe
                    size="mini"
                    cell-class-name="table-pro-cell"
                    header-row-class-name="table-pro-header"
                    on-header-dragend={this.headerDragend}
                    {...{
                        attrs: this.$attrs,
                        on: this.$listeners
                    }}
                >
                    {HeaderConfing}
                </el-table>

                {this.maskShown ? (
                    <div class="status-mask">
                        <i class="el-icon-error" />
                        <span>加载失败</span>
                    </div>
                ) : null}

                {this.paginationDef.show ? (
                    <div class="table-pro-pagination">
                        <el-pagination
                            on-current-change={this.handleCurrentChange}
                            current-page={this.currentPage}
                            page-size={this.pageSize}
                            page-sizes={this.paginationDef.pageSizes}
                            on-size-change={this.handleSizeChange}
                            layout={this.paginationDef.layout}
                            total={this.total}
                        />
                    </div>
                ) : null}
            </div>
        );
    }
};
</script>

<style lang="scss" scoped>
/deep/ .table-pro-drag-chosen-row-class {
    cursor: grab;
    .table-pro-cell {
        background-color: #dfecfb !important;
    }
}
/deep/ .table-pro-drag-ing-row-class {
    cursor: grab;
}
/deep/ .table-pro-drag-ghost-row-class {
    cursor: grab;
    .table-pro-cell {
        background-color: #dfecfb !important;
    }
}
.table-pro.sticky {
    /deep/ .el-table {
        overflow: visible;
        /deep/ .el-table__header-wrapper {
            position: -webkit-sticky;
            position: sticky;
            overflow-y: auto;
            z-index: 100; //避免列固定导致
        }
    }
}
.table-prefix {
    display: flex;
    justify-content: space-between;
    background-color: #fff;
    padding: 20px 0 10px 0;
    // padding: 20px 0 16px 0;
    position: relative;
    line-height: 32px;
    /deep/ .el-radio-button__orig-radio:checked + .el-radio-button__inner {
        background-color: #fff;
        color: #4187ff;
    }
}

.table-pro {
    position: relative;
    background-color: #fff;
    overflow: hidden;
    /deep/ .table-pro-header {
        th {
            background: #f5f6f7;
            font-size: 12px;
            line-height: 20px;
            padding: 10px 0;
            font-weight: bolder;
        }
    }
    /deep/ .table-pro-cell {
        font-size: 12px;
        line-height: 20px;
        padding: 10px 0;
        /deep/ button {
            font-size: 12px;
        }
        /deep/ .el-button + .el-button {
            margin: 0 3px;
        }
        /deep/ .xx-link .el-button--text,
        .el-button--text {
            padding-top: 0;
            padding-bottom: 0;
        }
    }
    /deep/ .el-table__body {
        table-layout: fixed;
    }
    .table-pro-pagination {
        padding: 8px 0;
    }
}

.status-mask {
    background: rgba(255, 255, 255, 0.9);
    position: absolute;
    top: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100% - 50px);
    color: #f56c6c;
    width: 100%;
    z-index: 10;
}

.back-to-top {
    background-color: #4187ff;
    border-radius: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.09);
    cursor: pointer;
    display: none;
    position: fixed;
    right: 20px;
    bottom: 80px;
    width: 32px;
    height: 28px;
    z-index: 10;
    text-align: center;
    line-height: 28px;
    .el-icon-caret-top {
        color: white;
    }
}
</style>
